import { useCallback, useEffect, useState } from "react"

type GuardState = {
  isPromptOpen: boolean
  guard: (action: () => void) => void
  confirm: () => void
  cancel: () => void
}

/**
 * Guards against losing unsaved changes. While `isDirty` is true:
 *   - `guard(action)` opens a confirmation prompt; the caller renders the
 *     <ConfirmationDialog> bound to `isPromptOpen` / `confirm` / `cancel`.
 *   - `beforeunload` is registered so the browser shows its native prompt on
 *     reload / tab close.
 *
 * This is a manual fallback for React Router's `useBlocker`, which requires a
 * data router (createBrowserRouter) — the patient-app uses the legacy
 * <BrowserRouter>, where `useBlocker` is a no-op / throws.
 */
export const useUnsavedChangesGuard = (isDirty: boolean): GuardState => {
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  useEffect(() => {
    if (!isDirty) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [isDirty])

  const guard = useCallback(
    (action: () => void) => {
      if (isDirty) {
        setPendingAction(() => action)
      } else {
        action()
      }
    },
    [isDirty],
  )

  const confirm = useCallback(() => {
    pendingAction?.()
    setPendingAction(null)
  }, [pendingAction])

  const cancel = useCallback(() => setPendingAction(null), [])

  return {
    isPromptOpen: pendingAction !== null,
    guard,
    confirm,
    cancel,
  }
}
